<script setup>

import { ref, reactive, computed, getCurrentInstance, watch, onMounted } from 'vue';
import { snapTo, clamp, prepareData, stateChecks, getElementColor, getElementAltColor, getPrimaryColor, getPrimaryAltColor, getPrimaryContrastColor, getSecondaryColor, getSecondaryAltColor, getSecondaryContrastColor, duplicate, debounce } from './misc.js';
import ContextMenu from './ContextMenu.vue';
import ContextMenuEntry from './ContextMenuEntry.vue';
import SvgHtml from './SvgHtml.vue';
import SvgButton from './SvgButton.vue';
import SvgImage from './SvgImage.vue';
import SvgCircle from './SvgCircle.vue';
import PropertyPage from './PropertyPage.vue';
import ImageSelect from './ImageSelect.vue';
import VideoSelect from './VideoSelect.vue';
import VimeoPlayer from '@vimeo/player';

const svgWidth = 1024,
  svgHeight = 550;

let instance = getCurrentInstance(),
  initial = instance.attrs['data-data'];

prepareData(initial);

let parent = instance.attrs['data-element'],
  s = reactive(initial),
  audioMediaIds = Object.values(Object.keys(s.assets).reduce((o, media_id) => {
    if (s.assets[media_id].type === 'audio') {
        o[media_id] = media_id;
    }

    return o;
  }, {})),
  dataElement = parent.nextElementSibling,
  editMode = Boolean(dataElement),
  d = reactive({
    activeElements: {},
    mouse: null,
    appElement: null,
    svgElement: null,
    assetElements: {},
    mediaElements: {},
    clipboard: null,
    clickActions: true,
    dragHandles: false,
    deletedElements: [],
    selectedLayer: getFirstLayer(),
    selectedElement: null,
    showDragHandles: true,
    contextMenuX: 0,
    contextMenuY: 0,
    contextMenuVisible: false,
    contextMenuWrapper: null,
    propertyPageVisible: editMode,
    dragging: false,
    xkey: undefined,
    ykey: undefined,
    currentSplash: null,
    assetSortReverse: true,
  });

if (s.layerSettings) {
  s.layerSettings = Object.assign([], s.layerSettings);
}
else {
  s.layerSettings = [];
}

window.addEventListener('click', deselect);
window.addEventListener('contextmenu', e => contextMenuClose());

if (editMode) {
  watch(s, debounce(() => {
    dataElement.value = JSON.stringify(s);
  }, 100), {
    deep: true,
    immediate: true,
  });
}

function storeElementRef (key, element, asset) {
  if (element) {
    d.assetElements[key] = {key, element, asset};
  }
  else {
    delete d.assetElements[key];
  }
}

function storeMediaRef (key, element) {
  if (element) {
    d.mediaElements[key] = d.mediaElements[key] || { canAutoplay: true, playing: false, element };
    d.mediaElements[key].element = element;

    if (s.globalSettings.assetVolume[key] !== undefined) {
      element.volume = s.globalSettings.assetVolume[key];
    }
  }
  else {
    delete d.mediaElements[key];
  }
}

function getVideo (element) {
  if (element && element.type === 'video') {
    let media_id = element.value;

    if (media_id) {
      let asset = s.assets[media_id];

      if (asset && asset.url) {
        return {[media_id]: asset};
      }
    }

    return {'none': null};
  }

  return {};
}

function getNoText() {
  return editMode ? '<span class="no-value">(No Text)</span>' : '';
}

function getLayerPopulation (extra = false, ...excludeLayers) {
  let pop;

  if (!s.elements || !s.elements.length) {
    pop = { '1': 0 };
  } else {
    pop = {};

    let min = Infinity, max = -Infinity;

    s.elements.forEach(element => {
      min = Math.min(min, element.layer || 0);
      max = Math.max(max, element.layer || 0);
    });

    if (extra) {
      min -= 1;
      max += 1;
    }

    min = Math.max(1, min);

    for (let c = min; c <= max ; c++) {
      pop[c] = 0;
    }

    s.elements.forEach(element => {
      element.layer = element.layer || 0;
      pop[element.layer]++;
    });
  }

  excludeLayers.forEach(excludeLayer => {
    if (excludeLayer !== undefined) {
      excludeLayer = excludeLayer.toString();

      if (pop.hasOwnProperty(excludeLayer)) {
        delete pop[excludeLayer];
      }
    }
  });

  return pop;
}

function getFirstLayer () {
  let pop = getLayerPopulation(false);

  for (let layer in pop) {
    if (pop[layer]) {
      return Number(layer);
    }
  }

  for (let layer in pop) {
    return Number(layer);
  }

  return 1;
}

function getModifiableStates (states) {
  let ret = {};

  Object.keys(states).forEach(key => {
    if (key.slice(0, 6) !== 'Audio:') {
      ret[key] = states[key];
    }
  });

  return ret;
}

function getAudioStates (states) {
  let ret = {};

  Object.keys(states).forEach(key => {
    if (key.slice(0, 6) === 'Audio:') {
      ret[key] = states[key];
    }
  });

  return ret;
}

function getAudioStateAsset (state) {
  if (state.slice(0, 6) === 'Audio:') {
    return s.assets[state.slice(6)];
  }

  return null;
}

function isObscured (obscurer, obscurable) {
  let ax1, ay1, ax2, ay2, bx1, by1, bx2, by2, ret;

  // Sort min/max and assign everything.
  [ax1, ax2] = [obscurer.x, obscurer.x + obscurer.width].sort((a, b) => a - b);
  [ay1, ay2] = [obscurer.y, obscurer.y + obscurer.height].sort((a, b) => a - b);
  [bx1, bx2] = [obscurable.x, obscurable.x + obscurable.width].sort((a, b) => a - b);
  [by1, by2] = [obscurable.y, obscurable.y + obscurable.height].sort((a, b) => a - b);

  return bx1 >= ax1 && by1 >= ay1 && bx2 <= ax2 && by2 <= ay2;
}

// Try to get some rough RTL reading order for items.
let elementKeys = computed(() => {
    let keys = Object.keys(s.elements).sort((a, b) => {
      [a, b] = [s.elements[a], s.elements[b]];

      return (a.z - b.z) || snapTo(a.y - b.y, 20) || snapTo(a.x - b.x, 20);
    });

    d.activeElements = {};

    keys.forEach(i => {
      d.activeElements[i] = elementActive(i);
    });

    for (let i = keys.length - 1, a; i >= 0 && keys[i] !== undefined; i--) {
      a = keys[i];

      if (d.activeElements[a]) {
        for (let j = i - 1, b; j >= 0 && keys[j] !== undefined; j--) {
          b = keys[j];

          if (d.activeElements[b]) {
            if (isObscured(s.elements[a], s.elements[b])) {
              d.activeElements[b] = 'obscured';
            }
          }
        }
      }
    }

    return keys;
  }),
  currentElement = computed(() => {
    if (d.selectedElement && s.elements[d.selectedElement]) {
      let states = s.elements[d.selectedElement].states = s.elements[d.selectedElement].states || {},
        stateFlags = {};

      for (let state in layerSettings.value.states) {
        stateFlags[state] = Boolean(states[state] || false);
      }

      s.elements[d.selectedElement].states = stateFlags;

      return s.elements[d.selectedElement];
    }

    return null;
  }),
  layerSettings = computed(() => {
    if (d.selectedLayer !== undefined && d.selectedLayer !== null) {
      s.layerSettings = s.layerSettings || {};
      s.layerSettings[d.selectedLayer] = s.layerSettings[d.selectedLayer] || {};
      let states = s.layerSettings[d.selectedLayer].statesAvailable = s.layerSettings[d.selectedLayer].statesAvailable || '',
        stateFlags = {};

      states.split(',').map(v => v.trim().toLowerCase()).filter(Boolean).forEach(state => {
        stateFlags[state] = Boolean(s.layerSettings[d.selectedLayer].states && s.layerSettings[d.selectedLayer].states[state]);
      });

      s.layerSettings[d.selectedLayer].states = stateFlags;

      Object.keys(d.mediaElements).forEach(media_id => {
        s.layerSettings[d.selectedLayer].states['Audio:' + media_id] = d.mediaElements[media_id].playing;
      });

      s.layerSettings[d.selectedLayer].name = s.layerSettings[d.selectedLayer].name || '';
      s.layerSettings[d.selectedLayer].autoplayAudio = s.layerSettings[d.selectedLayer].autoplayAudio || '';
      s.layerSettings[d.selectedLayer].splashTitleFontFamily = s.layerSettings[d.selectedLayer].splashTitleFontFamily || '';
      s.layerSettings[d.selectedLayer].splashTitle = s.layerSettings[d.selectedLayer].splashTitle || '';
      s.layerSettings[d.selectedLayer].splashSubtitleFontFamily = s.layerSettings[d.selectedLayer].splashSubtitleFontFamily || '';
      s.layerSettings[d.selectedLayer].splashSubtitle = s.layerSettings[d.selectedLayer].splashSubtitle || '';

      return s.layerSettings[d.selectedLayer];
    }

    return null;
  });

function getLayerName (layerKey) {
    if (layerKey !== undefined && layerKey !== null) {
      s.layerSettings = s.layerSettings || {};
      s.layerSettings[layerKey] = s.layerSettings[layerKey] || {};
      s.layerSettings[layerKey].name = s.layerSettings[layerKey].name || '';

      return s.layerSettings[layerKey].name;
    }

    return null;
}

function getSplashScreens () {
  if (layerSettings.value.splashTitle || layerSettings.value.splashSubtitle) {
    d.currentSplash = d.selectedLayer;
  }

  if (d.currentSplash !== null && (s.layerSettings[d.currentSplash].splashTitle || s.layerSettings[d.currentSplash].splashSubtitle)) {
    return {
      [d.currentSplash]: {
        titleFontFamily: (s.layerSettings[d.currentSplash].splashTitleFontFamily || ''),
        title: (s.layerSettings[d.currentSplash].splashTitle || ''),
        subtitleFontFamily: (s.layerSettings[d.currentSplash].splashSubtitleFontFamily || s.layerSettings[d.currentSplash].splashTitleFontFamily || ''),
        subtitle: (s.layerSettings[d.currentSplash].splashSubtitle || ''),
      }
    };
  }

  return {};
}

const wordsPerMinute = 80; // End of the year target for 3rd grade.
const secondsPerWord = 60 / wordsPerMinute;
const wordsPerLetter = 1 / 4.7; // Words have ~4.7 letters on average.
const secondsPerLetter = secondsPerWord * wordsPerLetter;

function getSplashLength(...strs) {
  let lettersPerSplash = strs.filter(Boolean).join('').replace(/\s/gi, '').length;
  let secondsPerSplash = lettersPerSplash * secondsPerLetter;
  console.log('Chars', lettersPerSplash);
  console.log('Len', secondsPerSplash);
  return secondsPerSplash;
}

function elementActive (i) {
  let element = s.elements[i];

  if (!element) {
    return false;
  }

  if (s.elements[i].layer != d.selectedLayer) {
    return false;
  }

  if (!Object.values(element.states).some(Boolean)) {
    return true;
  }

  if (!element.stateHandling || element.stateHandling.trim().toLowerCase() === 'default') {
    element.stateHandling = 'show for any';
  }

  if (!stateChecks[element.stateHandling]) {
    console.error('Invalid state handling type:', element.stateHandling);
    return true;
  }

  return stateChecks[element.stateHandling](element.states, layerSettings.value.states);
}

function clearActions (element) {
  while (element.action.length && !element.action[element.action.length - 1]) {
    element.action.pop();
  }
}

function addAction (action, element, e) {
  clearActions(element);
  element.action.push(action);

  if (e) {
    e.target.value = '';
  }
}

function getLinkActions () {
  let ret = {};

  for(let media_id in s.assets) {
    if (s.assets[media_id].type === 'document') {
      ret['open:' + media_id] = s.assets[media_id].name;
    }
  }

  return ret;
}

function elementCanHaveMultipleActions (element) {
  if (element.action) {
    for (let i in element.action) {
      if (element.action[i]) {
        if (element.action[i].slice(0, 5) === 'goto:') {
          return false;
        }

        if (element.action[i].slice(0, 5) === 'open:') {
          return false;
        }

        if (element.action[i].slice(0, 5) === 'url') {
          return false;
        }
      }
    }
  }

  return true;
}

function setState(state, value, clearOthers = false) {
  let toggle = value === 'toggle';
  value = Boolean(Number(value));
  clearOthers = Boolean(Number(clearOthers));

  if (clearOthers) {
    for (let key in layerSettings.value.states) {
      if (key === state) {
        if (toggle) {
          layerSettings.value.states[key] = !layerSettings.value.states[key];
        } else {
          layerSettings.value.states[key] = value;
        }
      } else {
        layerSettings.value.states[key] = false;
      }
    }
  } else {
    if (toggle) {
      layerSettings.value.states[state] = !layerSettings.value.states[state];
    } else {
      layerSettings.value.states[state] = value;
    }
  }
}

function executeGoto(newLayer) {
  if (newLayer === 'next') {
    switchLayer(d.selectedLayer + 1)
  } else if (newLayer === 'prev') {
    switchLayer(d.selectedLayer - 1);
  } else {
    switchLayer(newLayer);
  }

  clearCurrentElement();
}

function openFile(media_id) {
  let url = s.assets[media_id].url;
  window.open(url, '_blank');
}

function openUrl(url) {
  window.open(url, '_blank');
}

function disableDragHandles (e) {
  d.showDragHandles = false;
}

function enableDragHandles (e) {
  d.showDragHandles = true;
}

function doAction (e) {
  let index = d.selectedElement;

  if (!isFinite(index)) {
    throw new Error('Invalid index!');
  }

  if (d.clickActions && isFinite(index) && s.elements[index] && s.elements[index].action) {
    let ret = false;
    s.elements[index].action.filter(Boolean).forEach(action => {
      if (action) {
        let [command, args] = action.split(':');
        args = args ? args.split(',') : [];

        switch (command) {
          case 'goto':
            executeGoto(...args);
            ret = true;
            break;
          case 'state':
            setState(...args);
            ret = true;
            break;
          case 'open':
            openFile(...args);
            ret = true;
            break;
          case 'url':
            openUrl(s.elements[index].url);
            ret = true;
            break;
          case 'audio':
            toggleAudio(...args);
            ret = true;
            break;
          default:
            console.error('Unrecognized command:', command, ...args);
            break;
        }
      }
    });

    if (ret) {
      eatEvent(e);
    }

    return ret;
  }

  return false;
}

function click (e) {
  let index = d.selectedElement;

  if (!isFinite(index)) {
    throw new Error('Invalid index!');
  }

  d.contextMenuVisible = false;
  eatEvent(e);
}

function deselect (e) {
  clearCurrentElement();
  d.contextMenuVisible = false;
}

function focus (e, index) {
  setCurrentElement(index);
}

function setCurrentElement (index) {
  if (index === null || isFinite(index)) {
    d.selectedElement = index;
  }
}

function clearCurrentElement () {
  d.selectedElement = null;
  if (d.appElement && d.appElement.contains(document.activeElement)) {
    d.appElement.focus();
  }
}

function blur (e) {

}

function contextMenuOpen (e) {
  let index = d.selectedElement;

  if (d.contextMenuWrapper) {
    let wrapperRect = d.contextMenuWrapper.getBoundingClientRect();

    d.contextMenuVisible = true;
    d.contextMenuX = (e.clientX - wrapperRect.left)  / wrapperRect.width;
    d.contextMenuY = (e.clientY - wrapperRect.top) / wrapperRect.height;
  }

  eatEvent(e);
}

function contextMenuClose (e) {
  d.contextMenuVisible = false;
}

function addText (e) {
  s.elements.push({
    id: s.nextId++,
    type: 'text',
    color: '0-0-normal',
    hovercolor: '',
    fontFamily: '',
    fontSize: 'default',
    fontWeight: 'default',
    textAlign: 'default',
    padding: 1,
    lineHeight: 1.15,
    backgroundOpacity: 1,
    x: clamp(0, d.contextMenuX * svgWidth - 150 / 2, svgWidth - 150),
    y: clamp(0, d.contextMenuY * svgHeight - 75 / 2, svgHeight - 75),
    width: 150,
    height: 75,
    z: s.elements[d.selectedElement] ? s.elements[d.selectedElement].z + 1 : 0,
    layer: d.selectedLayer,
    value: '',
    action: [],
    states: s.elements[d.selectedElement] ? duplicate(s.elements[d.selectedElement].states) : {},
    stateHandling: s.elements[d.selectedElement] ? s.elements[d.selectedElement].stateHandling : 'show for any',
    nonhover: '',
    hover: '',
    transform: '',
    url: '',
  });

  s.elements = s.elements.filter(Boolean);
  setCurrentElement(s.elements.length - 1);
  d.contextMenuVisible = false;
}

function addButton (e) {
  s.elements.push({
    id: s.nextId++,
    type: 'button',
    color: s.primaryColor || '0-0-swap',
    hovercolor: '',
    fontFamily: s.fonts[11] ? 11 : '',
    fontSize: 'default',
    fontWeight: 'default',
    backgroundOpacity: 1,
    x: clamp(0, d.contextMenuX * svgWidth - 150 / 2, svgWidth - 150),
    y: clamp(0, d.contextMenuY * svgHeight - 75 / 2, svgHeight - 75),
    width: 140,
    height: 36,
    z: s.elements[d.selectedElement] ? s.elements[d.selectedElement].z + 1 : 0,
    layer: d.selectedLayer,
    value: 'CLICK HERE',
    action: [],
    states: s.elements[d.selectedElement] ? duplicate(s.elements[d.selectedElement].states) : {},
    stateHandling: s.elements[d.selectedElement] ? s.elements[d.selectedElement].stateHandling : 'show for any',
    nonhover: 'drop-shadow(0px 0px 1px black)',
    hover: '',
    transform: '',
    url: '',
  });

  s.elements = s.elements.filter(Boolean);
  setCurrentElement(s.elements.length - 1);
  d.contextMenuVisible = false;
}

function addImage (e) {
  s.elements.push({
    id: s.nextId++,
    type: 'image',
    color: '0-0-swap',
    hovercolor: '',
    backgroundOpacity: 1,
    x: clamp(0, d.contextMenuX * svgWidth - 300 / 2, svgWidth - 300),
    y: clamp(0, d.contextMenuY * svgHeight - 300 / 2, svgHeight - 300),
    width: 300,
    height: 300,
    z: s.elements[d.selectedElement] ? s.elements[d.selectedElement].z + 1 : -1,
    layer: d.selectedLayer,
    value: '',
    caption: '',
    preserveAspectRatio: 'xMidYMid meet',
    action: [],
    states: s.elements[d.selectedElement] ? duplicate(s.elements[d.selectedElement].states) : {},
    stateHandling: s.elements[d.selectedElement] ? s.elements[d.selectedElement].stateHandling : 'show for any',
    nonhover: '',
    hover: '',
    transform: '',
    url: '',
  });

  s.elements = s.elements.filter(Boolean);
  setCurrentElement(s.elements.length - 1);
  d.contextMenuVisible = false;
}

function addCircle (e) {
  s.elements.push({
    id: s.nextId++,
    type: 'circle',
    color: '0-0-swap',
    hovercolor: '',
    thickness: 3,
    backgroundOpacity: 1,
    x: clamp(0, d.contextMenuX * svgWidth - 25 / 2, svgWidth - 25),
    y: clamp(0, d.contextMenuY * svgHeight - 25 / 2, svgHeight - 25),
    width: 25,
    height: 25,
    z: s.elements[d.selectedElement] ? s.elements[d.selectedElement].z + 1 : 1,
    layer: d.selectedLayer,
    value: '',
    caption: '',
    action: [],
    states: s.elements[d.selectedElement] ? duplicate(s.elements[d.selectedElement].states) : {},
    stateHandling: s.elements[d.selectedElement] ? s.elements[d.selectedElement].stateHandling : 'show for any',
    nonhover: '',
    hover: '',
    transform: '',
    url: '',
  });

  s.elements = s.elements.filter(Boolean);
  setCurrentElement(s.elements.length - 1);
  d.contextMenuVisible = false;
}

function addVideo (e) {
  s.elements.push({
    id: s.nextId++,
    type: 'video',
    autoplay: false,
    color: '0-0-swap',
    backgroundOpacity: 1,
    x: clamp(0, d.contextMenuX * svgWidth - 480 / 2, svgWidth - 480),
    y: clamp(0, d.contextMenuY * svgHeight - 270 / 2, svgHeight - 270),
    width: 480,
    height: 270,
    z: s.elements[d.selectedElement] ? s.elements[d.selectedElement].z + 1 : 0,
    layer: d.selectedLayer,
    value: '',
    caption: '',
    action: [],
    states: s.elements[d.selectedElement] ? duplicate(s.elements[d.selectedElement].states) : {},
    stateHandling: s.elements[d.selectedElement] ? s.elements[d.selectedElement].stateHandling : 'show for any',
    nonhover: '',
    hover: '',
    transform: '',
    url: '',
  });

  s.elements = s.elements.filter(Boolean);
  setCurrentElement(s.elements.length - 1);
  d.contextMenuVisible = false;
}

function copyToClipboard (e) {
  d.clipboard = duplicate(s.elements[d.selectedElement]);
  d.contextMenuVisible = false;
}

function cutToClipboard (e) {
  d.clipboard = duplicate(s.elements[d.selectedElement]);
  s.elements[d.selectedElement] = null;
  s.elements = s.elements.filter(Boolean);
  d.svgElement.focus();
  d.contextMenuVisible = false;
}

function pasteFromClipboard (e) {
  if (d.clipboard) {
    let newElement = duplicate(d.clipboard);
    newElement.id = s.nextId++;
    newElement.layer = d.selectedLayer;

    if (s.elements[d.selectedElement]) {
      newElement.z = Math.max(newElement.z, s.elements[d.selectedElement].z + 1);
      newElement.states = duplicate(s.elements[d.selectedElement].states);
      newElement.stateHandling = s.elements[d.selectedElement].stateHandling;
    }

    newElement.action = newElement.action.filter(a => a.split(':')[0] !== 'state');

    console.log(newElement);

    s.elements.push(newElement);
    setCurrentElement(s.elements.length - 1);
    d.contextMenuVisible = false;
  }
}

function undoDelete () {
  if (d.deletedElements.length) {
    let newElement = JSON.parse(d.deletedElements.pop());
    s.elements.push(newElement);
    setCurrentElement(s.elements.length - 1);
    d.contextMenuVisible = false;
  }
}

function moveLayersByOffset (layerIndex, offset = 0) {
  layerIndex = Number(layerIndex);
  offset = Number(offset);

  if (offset) {
    for (let index in s.elements) {
      if (s.elements[index].layer >= layerIndex) {
        s.elements[index].layer = Math.max(s.elements[index].layer + offset, layerIndex);
      }

      if (s.elements[index].action && s.elements[index].action.length) {
        for (let aindex in s.elements[index].action) {
          let action = s.elements[index].action[aindex].split(':');

          if (action.length > 1 && action[0] === 'goto' && isFinite(action[1]) && Number(action[1]) >= layerIndex) {
            s.elements[index].action[aindex] = 'goto:' + Math.max(layerIndex, offset + Number(action[1]));
          }
        }
      }
    }

    let settings = s.layerSettings.splice(layerIndex);

    while (offset > 0) {
      settings.unshift({});
      offset--;
    }

    while (offset < 0) {
      settings.shift();
      offset++;
    }

    s.layerSettings.push(...settings);
  }
}

function deleteElement (...indexes) {
  if (indexes.length === 1) {
    d.deletedElements.push(JSON.stringify(s.elements[indexes[0]]));
  }

  if (indexes.length) {
    clearCurrentElement();

    indexes.forEach(index => {
      s.elements[index] = null;
    });

    s.elements = s.elements.filter(Boolean);
  }

  d.contextMenuVisible = false;
}

function deleteLayerElements (layerIndex, moveLayers = false) {
  if (confirm(moveLayers ? 'Are you sure you want to delete this layer? This cannot be undone!' : 'Are you sure you want to clear all items and settings for this layer? This cannot be undone!')) {
    let elements = [];

    s.elements.forEach((element, index) => {
      if (element.layer == layerIndex) {
        elements.push(index);
      }
    });

    deleteElement(...elements);

    if (moveLayers) {
      moveLayersByOffset(layerIndex, -1);
    }
  }
  else {
    d.contextMenuVisible = false;
  }
}

function swapLayers(layerIndexa, offset = 0) {
  layerIndexa = Number(layerIndexa);
  offset = Number(offset);

  let layerIndexb = layerIndexa + offset;

  if (s.layerSettings[layerIndexb]) {
    let a = s.layerSettings[layerIndexa],
      b = s.layerSettings[layerIndexb];

    s.layerSettings[layerIndexa] = b;
    s.layerSettings[layerIndexb] = a;

    s.elements.forEach(element => {
      if (element.layer == layerIndexa) {
        element.layer = layerIndexb;
      } else if (element.layer == layerIndexb) {
        element.layer = layerIndexa;
      }

      if (element.action && element.action.length) {
        for (let aindex in element.action) {
          let action = element.action[aindex].split(':');

          if (action.length > 1 && action[0] === 'goto' && isFinite(action[1])) {
            if (Number(action[1]) == layerIndexa) {
              action[1] = layerIndexb;
              element.action[aindex] = action.join(':');
            } else if (Number(action[1]) == layerIndexb) {
              action[1] = layerIndexa;
              element.action[aindex] = action.join(':');
            }
          }
        }
      }
    });

    d.selectedLayer = layerIndexb;
    d.contextMenuVisible = false;
  }
}

function addLayer (layerIndex, duplicate = false) {
  if (duplicate) {
    layerIndex = Number(layerIndex);

    let layerSettings = Object.assign(duplicate(s.layerSettings[layerIndex]), {
        name: (s.layerSettings[layerIndex].name || '').trim() ? s.layerSettings[layerIndex].name + ' (duplicate)' : '(duplicate)',
        splashTitle: '',
        splashSubtitle: '',
      }),
      toDuplicate = s.elements.filter(elem => elem.layer == layerIndex);

    if (layerIndex + 1 < s.layerSettings.length) {
      moveLayersByOffset(layerIndex + 1, 1);
      s.layerSettings[layerIndex + 1] = layerSettings;
    }
    else {
      s.layerSettings.push(layerSettings);
    }

    toDuplicate.forEach(elem => {
      let newElem = Object.assign(duplicate(elem), {
        id: s.nextId++,
        layer: layerIndex + 1,
      });

      s.elements.push(newElem);
    });
  }
  else {
    moveLayersByOffset(layerIndex, 1);
  }
}

function resize (dragData) {
  if (dragData.bothx) {
    s.elements[d.selectedElement].x = clamp(
      0,
      dragData.bothx - s.elements[d.selectedElement].width / 2,
      svgWidth - s.elements[d.selectedElement].width
    );
  }

  if (dragData.bothy) {
    s.elements[d.selectedElement].y = clamp(
      0,
      dragData.bothy - s.elements[d.selectedElement].height / 2,
      svgHeight - s.elements[d.selectedElement].height
    );
  }

  if (dragData.x) {
    let maxx = s.elements[d.selectedElement].x + s.elements[d.selectedElement].width;

    s.elements[d.selectedElement].x = clamp(
      0,
      dragData.x,
      maxx,
    );

    s.elements[d.selectedElement].width = maxx - s.elements[d.selectedElement].x;
  }

  if (dragData.y) {
    let maxy = s.elements[d.selectedElement].y + s.elements[d.selectedElement].height;

    s.elements[d.selectedElement].y = clamp(
      0,
      dragData.y,
      maxy,
    );

    s.elements[d.selectedElement].height = maxy - s.elements[d.selectedElement].y;
  }

  if (dragData.width) {
    let maxwidth = svgWidth - s.elements[d.selectedElement].x;

    s.elements[d.selectedElement].width = clamp(
      0,
      dragData.width - s.elements[d.selectedElement].x,
      maxwidth
    );
  }

  if (dragData.height) {
    let maxheight = svgHeight - s.elements[d.selectedElement].y;

    s.elements[d.selectedElement].height = clamp(
      0,
      dragData.height - s.elements[d.selectedElement].y,
      maxheight
    );
  }

}

function openProperties (e) {
  d.propertyPageVisible = true;
  d.contextMenuVisible = false;
  eatEvent(e);
}

function closeProperties (e) {
  d.propertyPageVisible = false;
  eatEvent(e);
}

function disableAudio (media_id, canAutoplay) {
  if (canAutoplay !== undefined) {
    d.mediaElements[media_id].canAutoplay = canAutoplay;
  }

  d.mediaElements[media_id].element.pause();
  d.mediaElements[media_id].element.currentTime = 0;
}

function enableAudio (media_id, canAutoplay) {
  if (canAutoplay !== undefined) {
    d.mediaElements[media_id].canAutoplay = canAutoplay;
  }

  if (s.globalSettings.assetVolume[media_id] !== undefined) {
    d.mediaElements[media_id].element.volume = s.globalSettings.assetVolume[media_id];
  }

  for (let other_media_id in d.mediaElements) {
    if (other_media_id != media_id && s.assets[other_media_id].type === 'audio') {
      disableAudio(other_media_id);
    }
  }

  d.mediaElements[media_id].element.play();
}

function toggleAudio (media_id, toggleAutoplay = true) {
  if (d.mediaElements[media_id].element.paused) {
    enableAudio(media_id, toggleAutoplay ? true : undefined);
  } else {
    disableAudio(media_id, toggleAutoplay ? false : undefined);
  }
}

function switchLayer (layerKey) {
  layerKey = Number(layerKey);

  if (layerKey < 1) {
    layerKey = 1;
  }

  d.contextMenuVisible = false;

  if (d.selectedLayer != layerKey) {
    d.selectedLayer = layerKey;
    clearCurrentElement();
    layerSettings.value.states = {};
  }

  Object.values(d.assetElements).forEach(entry => {
    if (entry.asset.type === 'video') {
      if (s.elements[entry.key].layer == layerKey) {
        if (s.elements[entry.key].autoplay) {
          entry.element.play();
        }
      }
      else {
        entry.element.pause();
      }
    }
    else if (entry.asset.type === 'remote_video') {
      if (entry.asset.provider === 'youtube') {
        if (s.elements[entry.key].layer == layerKey) {
          if (s.elements[entry.key].autoplay) {
            entry.element.contentWindow.postMessage(JSON.stringify({
              event: 'command',
              func: 'playVideo',
            }), 'https://www.youtube.com');
          }
        }
        else {
          entry.element.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: 'pauseVideo',
          }), 'https://www.youtube.com');
        }
      } else if (entry.asset.provider === 'vimeo') {
        if (s.elements[entry.key].layer == layerKey) {
          if (s.elements[entry.key].autoplay) {
            let player = new VimeoPlayer(entry.element);
            player.play();
          }
        }
        else {
          let player = new VimeoPlayer(entry.element);
          player.pause();
        }
      }
    }
  });

  Object.keys(d.mediaElements).forEach(media_id => {
    if (media_id === layerSettings.value.autoplayAudio) {
      if (d.mediaElements[media_id].canAutoplay) {
        enableAudio(media_id);
      }
    }
    else {
      disableAudio(media_id);
    }
  });
}

function prevLayer () {
  if (getLayerPopulation(true)[d.selectedLayer - 1] !== undefined) {
    switchLayer(d.selectedLayer - 1);
  }
}

function nextLayer () {
  if (getLayerPopulation(true)[d.selectedLayer + 1] !== undefined) {
    switchLayer(d.selectedLayer + 1);
  }
}

function eatEvent (e) {
  e.preventDefault();
  e.stopPropagation();
}

function ignoreEvent (e) {
  d.contextMenuVisible = false;
  e.stopPropagation();
}

function dragStart (xhandle, yhandle, e) {
  mouseevent(e);

  e.target.setPointerCapture(e.pointerId);

  d.dragging = true;
  d.xkey = xhandle;
  d.ykey = yhandle;
  eatEvent(e);
}

function dragUpdate (e) {
  mouseevent(e);

  if (d.dragging) {
    eatEvent(e);

    let rect = d.svgElement.getBoundingClientRect(),
      xscale = svgWidth / rect.width,
      yscale = svgHeight / rect.height,
      res = {};

    if (d.xkey !== null && d.xkey !== undefined) {
      res[d.xkey] = (e.clientX - rect.x) * xscale;
    }

    if (d.ykey !== null && d.ykey !== undefined) {
      res[d.ykey] = (e.clientY - rect.y) * yscale;
    }

    resize(res);
  }
}

function dragEnd (e) {
  mouseevent(e);

  if (d.dragging) {
    d.dragging = false;
    e.target.releasePointerCapture(e.pointerId);
    eatEvent(e);
  }
}

function checkHotkeys (e) {
  switch (e.keyCode) {
  case 27: // ESC
    if (d.contextMenuVisible) {
      d.contextMenuVisible = false;
    } else if (d.selectedElement) {
      clearCurrentElement();
    }

    break;
  case 37: // Left Arrow
  case 38: // Up Arrow
    if (e.ctrlKey && !e.shiftKey && !e.altKey) {
      prevLayer();
      eatEvent(e);
    }

    break;
  case 39: // Right Arrow
  case 40: // Down Arrow
    if (e.ctrlKey && !e.shiftKey && !e.altKey) {
      nextLayer();
      eatEvent(e);
    }

    break;
  default:
    break;
  }
}

function mouseevent (e) {
  let rect = d.svgElement.getBoundingClientRect();

  if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
    d.mouse = null;
  } else {
    d.mouse = {
      x: e.clientX,
      y: e.clientY,
    };
  }
}

Object.keys(s.globalSettings.assetVolume).forEach(media_id => {
  watch(() => s.globalSettings.assetVolume[media_id], () => {
    if (d.mediaElements && d.mediaElements[media_id] && d.mediaElements[media_id].element) {
      d.mediaElements[media_id].element.volume = s.globalSettings.assetVolume[media_id];
    }
  }, {
    immediate: true,
  });
});

function log (...args) {
  console.log(...args);
}

onMounted(() => {
  switchLayer(d.selectedLayer);
});

</script>

<template>
  <div :class="{
    'exhibit-frame-app': true,
    'edit-mode': editMode,
  }" @click="ignoreEvent" @keyup="checkHotkeys" @mouseover="appMouseOver" @mouseout="appMouseOut" :ref="el => { d.appElement = el; }">
    <audio v-for="media_id in audioMediaIds" :key="media_id" controls loop :src="s.assets[media_id].url" :ref="el => storeMediaRef(media_id, el)" @play="d.mediaElements[media_id].playing = !d.mediaElements[media_id].element.paused" @pause="d.mediaElements[media_id].playing = !d.mediaElements[media_id].element.paused"></audio>
    <div v-if="editMode" class="non-work-area top">
      <div class="stats-row">
        <div>{{ svgWidth }} x {{ svgHeight }}</div>
        <div>
          <label>Name of This Layer</label>
          <input type="text" size="30" v-model="layerSettings.name">
        </div>
        <div>
          <label>States Available on This Layer</label>
          <input type="text" size="30" v-model="layerSettings.statesAvailable">
        </div>
        <div>
          <label>Autoplay Audio</label>
          <select v-model="layerSettings.autoplayAudio">
            <option value="">No</option>
            <option v-for="media_id in audioMediaIds" :key="media_id" :value="media_id">{{ s.assets[media_id] ? s.assets[media_id].name : 'No Title' }}</option>
          </select>
        </div>
        <div><label>Allow Actions/Interactions on Click</label><input type="checkbox" v-model="d.clickActions" /></div>
        <div><label>Show Drag Handles</label><input type="checkbox" v-model="d.dragHandles" /></div>
        <div>
          <select :value="d.selectedLayer" @change="e => switchLayer(e.target.value)">
            <option v-for="(population, layerKey) in getLayerPopulation(true)" :key="layerKey" :value="layerKey">Layer {{ layerKey }}<template v-if="getLayerName(layerKey)"> [{{ getLayerName(layerKey) }}]</template><template v-if="!population"> (empty)</template></option>
          </select>
        </div>
      </div>
      <details>
        <summary>Splash Screen</summary>
        <div :style="{ display: 'flex', position: 'absolute', flexWrap: 'wrap', backgroundColor: '#e0e0e0', borderRadius: '3px', boxShadow: '4px 3px 6px black', zIndex: 500, width: '345px' }">
          <span class="splash-input" :style="{ flex: '1 1 auto', display: 'flex' }">
            <label>Title Font</label>
            <select v-model="layerSettings.splashTitleFontFamily">
              <option value="">Default</option>
              <option v-for="font in Object.values(s.fonts).sort((a, b) => Number(a.order || 0) - Number(b.order || 0))" :key="font.tid" :value="font.tid">{{ font.name }}</option>
            </select>
          </span>
          <span class="splash-input" :style="{ flex: '1 1 auto', display: 'flex' }">
            <label>Title</label>
            <textarea size="50" cols="25" rows="2" v-model="layerSettings.splashTitle" :style="{ width: 'initial' }" />
          </span>
          <span class="splash-input" :style="{ flex: '1 1 auto', display: 'flex' }">
            <label>Sub-title Font</label>
            <select v-model="layerSettings.splashSubtitleFontFamily">
              <option value="">Default</option>
              <option v-for="font in Object.values(s.fonts).sort((a, b) => Number(a.order || 0) - Number(b.order || 0))" :key="font.tid" :value="font.tid">{{ font.name }}</option>
            </select>
          </span>
          <span class="splash-input" :style="{ flex: '1 1 auto', display: 'flex' }">
            <label>Sub-title</label>
            <textarea size="50" cols="25" rows="2" v-model="layerSettings.splashSubtitle" :style="{ width: 'initial' }" />
          </span>
        </div>
      </details>
      <details v-if="s.globalSettings.assetVolume && Object.keys(s.globalSettings.assetVolume).length" :style="{ position: 'relative', zIndex: 499 }">
        <summary>Audio Volume</summary>
        <div :style="{ display: 'flex', position: 'absolute', flexWrap: 'wrap', backgroundColor: '#e0e0e0', borderRadius: '3px', boxShadow: '4px 3px 6px black', zIndex: 500 }">
          <span v-for="media_id in audioMediaIds" :key="media_id" class="volume-control">
            <label>"{{ s.assets[media_id] ? s.assets[media_id].name : 'No Title' }}" Volume <span v-text="Math.round(s.globalSettings.assetVolume[media_id] * 100) + '%'" :style="{ display: 'inline-block', width: '2.6em' }" /></label>
            <input type="range" v-model="s.globalSettings.assetVolume[media_id]" min="0" max="1" step="0.01">
          </span>
        </div>
      </details>
    </div>
    <div class="work-area" @mousedown="disableDragHandles" @mouseup="enableDragHandles">
      <svg :ref="el => { d.svgElement = el; }" class="exhibit-frame-svg interact-element" :viewBox="[0, 0, svgWidth, svgHeight].join(' ')" tabindex="0" aria-label="Exhibit Frame Content" @click.left="e => doAction(e) || click(e)" @keypress.enter="e => doAction(e)" @keypress.space="e => doAction(e)" @contextmenu="e => editMode && contextMenuOpen(e)" @mousedown="disableDragHandles" @mouseup="enableDragHandles" @mousemove="mouseevent" @mouseenter="mouseevent" @mouseleave="mouseevent">
        <defs>
          <radialGradient id="glossyGradient" cx="0.5" cy="1" r="1.5">
            <stop offset="0%" stop-color="#ffffff" stop-opacity="0" />
            <stop offset="49%" stop-color="#ffffff" stop-opacity="0" />
            <stop offset="51%" stop-color="#ffffff" stop-opacity="0.22" />
            <stop offset="100%" stop-color="#ffffff" stop-opacity="0.93" />
          </radialGradient>
          <filter id="outlineOnly">
            <feFlood result="mask" flood-color="#000" />
            <feComposite in="mask" in2="SourceGraphic" operator="in" result="mask1" />
            <feMorphology in="mask1" result="mask" operator="dilate" radius="1.5" />
            <feMorphology in="mask1" result="mask2" operator="erode" radius="1.5" />

            <feComposite in="mask" in2="mask1" operator="out" result="mask" />
            <feComposite in="mask1" in2="mask2" operator="out" result="mask1" />

            <feFlood result="black" flood-color="#black" />
            <feComposite in="black" in2="mask" operator="in" result="mask" />

            <feFlood result="white" flood-color="#fff" />
            <feComposite in="white" in2="mask1" operator="in" result="mask1" />

            <feMerge>
              <feMergeNode in="mask" />
              <feMergeNode in="mask1" />
            </feMerge>
          </filter>

          <filter id="outline">
            <feFlood result="mask" flood-color="#000" />
            <feComposite in="mask" in2="SourceGraphic" operator="in" result="mask1" />
            <feMorphology in="mask1" result="mask" operator="dilate" radius="1.5" />
            <feMorphology in="mask1" result="mask2" operator="erode" radius="1.5" />

            <feComposite in="mask" in2="mask1" operator="out" result="mask" />
            <feComposite in="mask1" in2="mask2" operator="out" result="mask1" />

            <feFlood result="black" flood-color="#black" />
            <feComposite in="black" in2="mask" operator="in" result="mask" />

            <feFlood result="white" flood-color="#fff" />
            <feComposite in="white" in2="mask1" operator="in" result="mask1" />

            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="mask" />
              <feMergeNode in="mask1" />
            </feMerge>
          </filter>

          <filter id="noRender">
          </filter>
        </defs>
        <g :key="elementIndex" :data-element-id="s.elements[elementIndex] && s.elements[elementIndex].id" v-for="elementIndex in elementKeys">
          <template v-if="s.elements[elementIndex]">
            <SvgHtml v-if="s.elements[elementIndex].type === 'text'" :data="s.elements[elementIndex]" :visible="d.activeElements[elementIndex]" :colors="s.colors" :editable="editMode" @focus="e => focus(e, elementIndex)">
              <div :class="{
                'svg-text': true,
                ['fs-' + s.elements[elementIndex].fontSize]: true,
                ['fw-' + s.elements[elementIndex].fontWeight]: true,
                ['ta-' + s.elements[elementIndex].textAlign]: true,
              }" v-html="s.elements[elementIndex].value || (editMode ? getNoText() : '')" :style="{
                padding: s.elements[elementIndex].padding + 'em',
                fontFamily: s.elements[elementIndex].fontFamily && s.fonts[s.elements[elementIndex].fontFamily] && s.fonts[s.elements[elementIndex].fontFamily].fontFamily || undefined,
                lineHeight: s.elements[elementIndex].lineHeight,
              }" :data-font-family="s.elements[elementIndex].fontFamily && s.fonts[s.elements[elementIndex].fontFamily] && s.fonts[s.elements[elementIndex].fontFamily].fontFamily || undefined" />
            </SvgHtml>
            <SvgButton v-else-if="s.elements[elementIndex].type === 'button'" :data="s.elements[elementIndex]" :visible="d.activeElements[elementIndex]" :colors="s.colors" :fonts="s.fonts" :editable="editMode" @focus="e => focus(e, elementIndex)" />
            <SvgImage v-else-if="s.elements[elementIndex].type === 'image'" :data="s.elements[elementIndex]" :visible="d.activeElements[elementIndex]" :colors="s.colors" :assets="s.assets" :editable="editMode" @focus="e => focus(e, elementIndex)" />
            <SvgCircle v-else-if="s.elements[elementIndex].type === 'circle'" :data="s.elements[elementIndex]" :visible="d.activeElements[elementIndex]" :colors="s.colors" :assets="s.assets" :editable="editMode" @focus="e => focus(e, elementIndex)" />
            <SvgHtml v-for="(asset, media_id) in getVideo(s.elements[elementIndex])" :key="media_id + '-' + d.activeElements[elementIndex]" :data="s.elements[elementIndex]" :visible="d.activeElements[elementIndex]" :colors="s.colors" :editable="editMode" :disableInteract="!d.clickActions" @focus="e => focus(e, elementIndex)">
              <div class="svg-video" v-if="asset">
                <video v-if="asset.type === 'video'"
                  :width="s.elements[elementIndex].width"
                  :height="s.elements[elementIndex].height"
                  autobuffer
                  :autoplay="s.elements[elementIndex].autoplay ? 'autoplay' : false"
                  controls
                  preload="auto"
                  :src="asset.url"
                  :ref="el => storeElementRef(elementIndex, el, asset)" />
                <iframe v-else-if="asset.type === 'remote_video' && asset.provider === 'youtube'"
                  :width="s.elements[elementIndex].width"
                  :height="s.elements[elementIndex].height"
                  :src="'https://www.youtube.com/embed/' + asset.code + '?autoplay=' + (s.elements[elementIndex].autoplay ? '1' : '0') + '&modestbranding=1&rel=0&enablejsapi=1'"
                  title="YouTube video player"
                  allow="fullscreen; autoplay; picture-in-picture"
                  :ref="el => storeElementRef(elementIndex, el, asset)"></iframe>
                <iframe v-else-if="asset.type === 'remote_video' && asset.provider === 'vimeo'"
                  :width="s.elements[elementIndex].width"
                  :height="s.elements[elementIndex].height"
                  :src="'https://player.vimeo.com/video/' + asset.code + '?autoplay=' + (s.elements[elementIndex].autoplay ? '1' : '0') + '&title=0&portrait=0&byline=0&color=' + getElementColor(s.elements[elementIndex], s.colors).slice(1)"
                  title="Vimeo video player"
                  allow="fullscreen; autoplay; picture-in-picture"
                  :ref="el => storeElementRef(elementIndex, el, asset)"></iframe>
              </div>
              <div class="svg-video" v-else><span class="no-value">(No Video)</span></div>
            </SvgHtml>
          </template>
        </g>
        <template v-if="d.dragging || d.dragHandles && d.showDragHandles && editMode && d.selectedElement">
          <rect class="drag-handle" :x="currentElement.x + currentElement.width / 2 - 8" :y="currentElement.y + currentElement.height / 2 - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @pointerdown="e => dragStart('bothx', 'bothy', e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
          <rect class="drag-handle ew" :x="currentElement.x - 8" :y="currentElement.y + currentElement.height / 2 - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @pointerdown="e => dragStart('x', null, e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
          <rect class="drag-handle ew" :x="currentElement.x + currentElement.width - 8" :y="currentElement.y + currentElement.height / 2 - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @pointerdown="e => dragStart('width', null, e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
          <rect class="drag-handle ns" :x="currentElement.x + currentElement.width / 2 - 8" :y="currentElement.y - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @pointerdown="e => dragStart(null, 'y', e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
          <rect class="drag-handle ns" :x="currentElement.x + currentElement.width / 2 - 8" :y="currentElement.y + currentElement.height - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @pointerdown="e => dragStart(null, 'height', e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
          <rect class="drag-handle nwse" :x="currentElement.x - 8" :y="currentElement.y - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @pointerdown="e => dragStart('x', 'y', e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
          <rect class="drag-handle nesw" :x="currentElement.x + currentElement.width - 8" :y="currentElement.y - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @pointerdown="e => dragStart('width', 'y', e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
          <rect class="drag-handle nesw" :x="currentElement.x - 8" :y="currentElement.y + currentElement.height - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @pointerdown="e => dragStart('x', 'height', e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
          <rect class="drag-handle nwse" :x="currentElement.x + currentElement.width - 8" :y="currentElement.y + currentElement.height - 8" width="16" height="16" @click.left="eatEvent" @contextmenu="eatEvent" @mousedown="eatEvent" @mousemove="eatEvent" @mouseup="ignoreClick" @pointerdown="e => dragStart('width', 'height', e)" @pointermove="dragUpdate" @pointerup="dragEnd" :opacity="d.dragging ? 0 : 0.6667" />
        </template>
        <g v-for="(splash, layerIndex) in getSplashScreens()" :key="layerIndex" class="splash-screen-group no-interact">
          <foreignObject x="0" y="0" :width="svgWidth" :height="svgHeight">
            <div xmlns="http://www.w3.org/1999/xhtml" class="splash-screen no-interact" role="none" :tabindex="-1" :style="{
              'animation-delay': Math.max(0, getSplashLength(splash.title, splash.subtitle) - 3) + 's',
              backgroundColor: getPrimaryColor(s),
            }">
              <div class="title-wrapper">
                <div class="title" v-html="splash.title" :class="{
                  'fs-largest': !!splash.subtitle,
                  'fs-title': !splash.subtitle,
                }" :style="{
                  color: getPrimaryAltColor(s),
                  fontFamily: splash.titleFontFamily && s.fonts[splash.titleFontFamily] && s.fonts[splash.titleFontFamily].fontFamily || undefined,
                }" />
                <div class="subtitle" v-html="splash.subtitle" :class="{
                  'fs-larger': true,
                }" :style="{
                  color: getPrimaryContrastColor(s),
                  fontFamily: splash.subtitleFontFamily && s.fonts[splash.subtitleFontFamily] && s.fonts[splash.subtitleFontFamily].fontFamily || undefined,
                }" />
              </div>
            </div>
          </foreignObject>
        </g>
      </svg>
      <div :ref="el => { d.contextMenuWrapper = el; }" class="exhibit-frame-overlay">
        <ContextMenu :key="currentElement ? currentElement.id : 'none'" :visible="d.contextMenuVisible" :x="d.contextMenuX" :y="d.contextMenuY">
          <template v-if="!d.propertyPageVisible">
            <ContextMenuEntry v-if="d.selectedElement !== null" @select="openProperties">Properties</ContextMenuEntry>
            <div v-if="d.selectedElement !== null" class="menu-divider"></div>
          </template>
          <ContextMenuEntry @select="addText">Add Text</ContextMenuEntry>
          <ContextMenuEntry @select="addButton">Add Button</ContextMenuEntry>
          <ContextMenuEntry @select="addImage">Add Image</ContextMenuEntry>
          <ContextMenuEntry @select="addCircle">Add Circle</ContextMenuEntry>
          <ContextMenuEntry @select="addVideo">Add Video</ContextMenuEntry>
          <ContextMenuEntry v-if="d.selectedElement !== null" @select="copyToClipboard">Copy {{ currentElement.type }}</ContextMenuEntry>
          <ContextMenuEntry v-if="d.selectedElement !== null" @select="cutToClipboard">Cut {{ currentElement.type }}</ContextMenuEntry>
          <ContextMenuEntry v-if="d.clipboard" @select="pasteFromClipboard">Paste {{ d.clipboard.type }}</ContextMenuEntry>
          <div class="menu-divider"></div>
          <ContextMenuEntry @select="prevLayer">Previous Layer (CTRL + &larr;)</ContextMenuEntry>
          <ContextMenuEntry @select="nextLayer">Next Layer (CTRL + &rarr;)</ContextMenuEntry>
          <ContextMenuEntry v-for="(checked, state) in getModifiableStates(layerSettings.states)" :checked="checked" :key="state" @select="layerSettings.states[state] = !checked">State: {{ state }}</ContextMenuEntry>
          <div class="menu-divider"></div>
          <ContextMenuEntry @select="swapLayers(d.selectedLayer, -1)">Move Layer Up</ContextMenuEntry>
          <ContextMenuEntry @select="swapLayers(d.selectedLayer, 1)">Move Layer Down</ContextMenuEntry>
          <ContextMenuEntry @select="addLayer(d.selectedLayer)">Insert Layer</ContextMenuEntry>
          <ContextMenuEntry @select="addLayer(d.selectedLayer, true)">Duplicate Layer</ContextMenuEntry>
          <ContextMenuEntry @select="deleteLayerElements(d.selectedLayer)">Clear Layer</ContextMenuEntry>
          <ContextMenuEntry @select="deleteLayerElements(d.selectedLayer, true)">Delete This Layer</ContextMenuEntry>
          <div class="menu-divider"></div>
          <ContextMenuEntry v-if="d.deletedElements.length" @select="undoDelete">Undelete Last Element</ContextMenuEntry>
          <ContextMenuEntry v-if="d.selectedElement !== null" @select="deleteElement(d.selectedElement)">Delete Item</ContextMenuEntry>
        </ContextMenu>
      </div>
    </div>
    <div v-if="editMode" class="non-work-area right">
      <PropertyPage v-for="(element, index) in s.elements" :key="index + '-' + element.id" :visible="element && currentElement && d.propertyPageVisible && element.id === currentElement.id" :showTitle="false" :titleExtra="element.id" @close="closeProperties">
        <div v-if="currentElement.type === 'image' || currentElement.type === 'circle'">
          <label>Caption (for screen readers)</label>
          <div>
            <input type="text" v-model="element.caption" style="width:100%">
          </div>
        </div>
        <div class="property-table">
          <template v-if="element.type === 'video'">
            <div>
              <label>Autoplay</label>
              <div>
                <select class="autoplay-select" v-model="element.autoplay">
                  <option :value="false">No</option>
                  <option :value="true">Yes</option>
                </select>
              </div>
            </div>
          </template>
          <div>
            <label>Normal Color</label>
            <div>
              <select v-model="element.color">
                <option v-for="(color, color_id) in s.colors" :value="color_id" :key="color_id">{{ color.name }}</option>
              </select>
            </div>
          </div>
          <template v-if="currentElement.type !== 'video'">
            <div>
              <label>Hover Color</label>
              <div>
                <select v-model="element.hovercolor">
                  <option value="">Same as Normal Color</option>
                  <option v-for="(color, color_id) in s.colors" :value="color_id" :key="color_id">{{ color.name }}</option>
                </select>
              </div>
            </div>
          </template>
          <template v-if="currentElement.type === 'circle'">
            <div>
              <label>Outline Thickness</label>
              <div>
                <input type="number" v-model="element.thickness" min="0" step="0.25">
              </div>
            </div>
          </template>
          <div>
            <label>Background Opacity</label>
            <div>
              <input type="number" v-model="element.backgroundOpacity" min="0" max="1" step="0.05">
            </div>
          </div>
          <div>
            <label>Normal Effect</label>
            <div>
              <select v-model="element.nonhover">
                <option value="">No Effect</option>
                <option value="brightness(1.3)">Lighten 30%</option>
                <option value="brightness(1.2)">Lighten 20%</option>
                <option value="brightness(1.1)">Lighten 10%</option>
                <option value="brightness(0.9)">Darken 10%</option>
                <option value="brightness(0.8)">Darken 20%</option>
                <option value="brightness(0.7)">Darken 30%</option>
                <option value="grayscale(1)">Greyscale</option>
                <option value="sepia(1)">Sepia</option>
                <option value="invert(1)">Invert</option>
                <option value="hue-rotate(60deg)">Shift Hue 60°</option>
                <option value="hue-rotate(120deg)">Shift Hue 120°</option>
                <option value="hue-rotate(180deg)">Shift Hue 180°</option>
                <option value="hue-rotate(-120deg)">Shift Hue -120°</option>
                <option value="hue-rotate(-60deg)">Shift Hue -60°</option>
                <option value="blur(3px)">Blur</option>
                <option value="opacity(0)">Invisible, but still clickable</option>
                <option value="drop-shadow(-1px 0px 0px #0004) drop-shadow(1px 0px 0px #0004) drop-shadow(0px -1px 0px #0004) drop-shadow(0px 1px 0px #0004)">Outline (black, 25%)</option>
                <option value="drop-shadow(-1px 0px 0px #0008) drop-shadow(1px 0px 0px #0008) drop-shadow(0px -1px 0px #0008) drop-shadow(0px 1px 0px #0008)">Outline (black, 50%)</option>
                <option value="drop-shadow(-1px 0px 0px #000c) drop-shadow(1px 0px 0px #000c) drop-shadow(0px -1px 0px #000c) drop-shadow(0px 1px 0px #000c)">Outline (black, 75%)</option>
                <option value="drop-shadow(-1px 0px 0px #000) drop-shadow(1px 0px 0px #000) drop-shadow(0px -1px 0px #000) drop-shadow(0px 1px 0px #000)">Outline (black, 100%)</option>
                <option value="drop-shadow(0px 0px 1px black)">Drop Shadow (black, light)</option>
                <option value="drop-shadow(0px 0px 2px black)">Drop Shadow (black)</option>
                <option value="drop-shadow(0px 0px 5px black)">Drop Shadow (black, heavy)</option>
                <option value="drop-shadow(-1px 0px 0px #fff4) drop-shadow(1px 0px 0px #fff4) drop-shadow(0px -1px 0px #fff4) drop-shadow(0px 1px 0px #fff4)">Outline (white, 25%)</option>
                <option value="drop-shadow(-1px 0px 0px #fff8) drop-shadow(1px 0px 0px #fff8) drop-shadow(0px -1px 0px #fff8) drop-shadow(0px 1px 0px #fff8)">Outline (white, 50%)</option>
                <option value="drop-shadow(-1px 0px 0px #fffc) drop-shadow(1px 0px 0px #fffc) drop-shadow(0px -1px 0px #fffc) drop-shadow(0px 1px 0px #fffc)">Outline (white, 75%)</option>
                <option value="drop-shadow(-1px 0px 0px #fff) drop-shadow(1px 0px 0px #fff) drop-shadow(0px -1px 0px #fff) drop-shadow(0px 1px 0px #fff)">Outline (white, 100%)</option>
                <option value="drop-shadow(0px 0px 1px white)">Drop Shadow (white, light)</option>
                <option value="drop-shadow(0px 0px 2px white)">Drop Shadow (white)</option>
                <option value="drop-shadow(0px 0px 5px white)">Drop Shadow (white, heavy)</option>
              </select>
            </div>
          </div>
          <div>
            <label>Hover Effect</label>
            <div>
              <select v-model="element.hover">
                <option value="">Same as Normal Effect</option>
                <option value="none">No Effect</option>
                <option value="brightness(1.3)">Lighten 30%</option>
                <option value="brightness(1.2)">Lighten 20%</option>
                <option value="brightness(1.1)">Lighten 10%</option>
                <option value="brightness(0.9)">Darken 10%</option>
                <option value="brightness(0.8)">Darken 20%</option>
                <option value="brightness(0.7)">Darken 30%</option>
                <option value="grayscale(1)">Greyscale</option>
                <option value="sepia(1)">Sepia</option>
                <option value="invert(1)">Invert</option>
                <option value="hue-rotate(60deg)">Shift Hue 60°</option>
                <option value="hue-rotate(120deg)">Shift Hue 120°</option>
                <option value="hue-rotate(180deg)">Shift Hue 180°</option>
                <option value="hue-rotate(-120deg)">Shift Hue -120°</option>
                <option value="hue-rotate(-60deg)">Shift Hue -60°</option>
                <option value="blur(3px)">Blur</option>
                <option value="opacity(0)">Invisible, but still clickable</option>
                <option value="drop-shadow(-1px 0px 0px #0004) drop-shadow(1px 0px 0px #0004) drop-shadow(0px -1px 0px #0004) drop-shadow(0px 1px 0px #0004)">Outline (black, 25%)</option>
                <option value="drop-shadow(-1px 0px 0px #0008) drop-shadow(1px 0px 0px #0008) drop-shadow(0px -1px 0px #0008) drop-shadow(0px 1px 0px #0008)">Outline (black, 50%)</option>
                <option value="drop-shadow(-1px 0px 0px #000c) drop-shadow(1px 0px 0px #000c) drop-shadow(0px -1px 0px #000c) drop-shadow(0px 1px 0px #000c)">Outline (black, 75%)</option>
                <option value="drop-shadow(-1px 0px 0px #000) drop-shadow(1px 0px 0px #000) drop-shadow(0px -1px 0px #000) drop-shadow(0px 1px 0px #000)">Outline (black, 100%)</option>
                <option value="drop-shadow(0px 0px 1px black)">Drop Shadow (black, light)</option>
                <option value="drop-shadow(0px 0px 2px black)">Drop Shadow (black)</option>
                <option value="drop-shadow(0px 0px 5px black)">Drop Shadow (black, heavy)</option>
                <option value="drop-shadow(-1px 0px 0px #fff4) drop-shadow(1px 0px 0px #fff4) drop-shadow(0px -1px 0px #fff4) drop-shadow(0px 1px 0px #fff4)">Outline (white, 25%)</option>
                <option value="drop-shadow(-1px 0px 0px #fff8) drop-shadow(1px 0px 0px #fff8) drop-shadow(0px -1px 0px #fff8) drop-shadow(0px 1px 0px #fff8)">Outline (white, 50%)</option>
                <option value="drop-shadow(-1px 0px 0px #fffc) drop-shadow(1px 0px 0px #fffc) drop-shadow(0px -1px 0px #fffc) drop-shadow(0px 1px 0px #fffc)">Outline (white, 75%)</option>
                <option value="drop-shadow(-1px 0px 0px #fff) drop-shadow(1px 0px 0px #fff) drop-shadow(0px -1px 0px #fff) drop-shadow(0px 1px 0px #fff)">Outline (white, 100%)</option>
                <option value="drop-shadow(0px 0px 1px white)">Drop Shadow (white, light)</option>
                <option value="drop-shadow(0px 0px 2px white)">Drop Shadow (white)</option>
                <option value="drop-shadow(0px 0px 5px white)">Drop Shadow (white, heavy)</option>
              </select>
            </div>
          </div>
          <div>
            <label>Transform</label>
            <div>
              <select v-model="element.transform">
                <option value="">No Effect</option>
                <option value="scale(-1 1)">Flip Horizontally</option>
                <option value="scale(1 -1)">Flip Vertically</option>
                <option value="rotate(-30)">Rotate -30°</option>
                <option value="rotate(-15)">Rotate -15°</option>
                <option value="rotate(15)">Rotate 15°</option>
                <option value="rotate(30)">Rotate 30°</option>
              </select>
            </div>
          </div>
          <template v-if="currentElement.type === 'text'">
            <div>
              <label>Padding</label>
              <div>
                <input type="number" v-model="element.padding" min="0" step="0.25">
              </div>
            </div>
            <div>
              <label>Line Height</label>
              <div>
                <input type="number" v-model="element.lineHeight" min="1" step="0.05" max="2">
              </div>
            </div>
            <div>
              <label>Font</label>
              <div>
                <select v-model="element.fontFamily">
                  <option value="">Default</option>
                  <option v-for="font in Object.values(s.fonts).sort((a, b) => Number(a.order || 0) - Number(b.order || 0))" :key="font.tid" :value="font.tid">{{ font.name }}</option>
                </select>
              </div>
            </div>
            <div>
              <label>Font Size</label>
              <div>
                <select v-model="element.fontSize">
                  <option value="smallest">Smallest</option>
                  <option value="small">Small</option>
                  <option value="default">Default</option>
                  <option value="bigger">Bigger</option>
                  <option value="large">Large</option>
                  <option value="larger">Larger</option>
                  <option value="largest">Largest</option>
                </select>
              </div>
            </div>
            <div>
              <label>Font Weight</label>
              <div>
                <select v-model="element.fontWeight">
                  <option value="default">Default</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
            </div>
            <div>
              <label>Text Align</label>
              <div>
                <select v-model="element.textAlign">
                  <option value="default">Default</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                  <option value="justify">Justify</option>
                </select>
              </div>
            </div>
          </template>
          <template v-if="currentElement.type === 'button'">
            <div>
              <label>Font</label>
              <div>
                <select v-model="element.fontFamily">
                  <option value="">Default</option>
                  <option v-for="font in Object.values(s.fonts).sort((a, b) => Number(a.order || 0) - Number(b.order || 0))" :key="font.tid" :value="font.tid">{{ font.name }}</option>
                </select>
              </div>
            </div>
            <div>
              <label>Font Size</label>
              <div>
                <select v-model="element.fontSize">
                  <option value="smallest">Smallest</option>
                  <option value="small">Small</option>
                  <option value="default">Default</option>
                  <option value="bigger">Bigger</option>
                  <option value="large">Large</option>
                  <option value="larger">Larger</option>
                  <option value="largest">Largest</option>
                </select>
              </div>
            </div>
            <div>
              <label>Font Weight</label>
              <div>
                <select v-model="element.fontWeight">
                  <option value="default">Default</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
            </div>
          </template>
          <template v-if="element.type === 'image'">
            <div>
              <label>Image Scaling</label>
              <div>
                <select class="aspect-ratio-select" v-model="element.preserveAspectRatio">
                  <option value="xMidYMid slice">Preserve Aspect Ratio (Fill)</option>
                  <option value="xMidYMin slice">Preserve Aspect Ratio (Fill, Align Top)</option>
                  <option value="xMinYMid slice">Preserve Aspect Ratio (Fill, Align Left)</option>
                  <option value="xMaxYMid slice">Preserve Aspect Ratio (Fill, Align Right)</option>
                  <option value="xMidYMax slice">Preserve Aspect Ratio (Fill, Align Bottom)</option>
                  <option value="xMidYMid meet">Preserve Aspect Ratio</option>
                  <option value="xMidYMin meet">Preserve Aspect Ratio (Align Top)</option>
                  <option value="xMinYMid meet">Preserve Aspect Ratio (Align Left)</option>
                  <option value="xMaxYMid meet">Preserve Aspect Ratio (Align Right)</option>
                  <option value="xMidYMax meet">Preserve Aspect Ratio (Align Bottom)</option>
                  <option value="none">Stretch</option>
                </select>
              </div>
            </div>
          </template>
          <div>
            <label>X</label>
            <div>
              <input type="number" v-model="element.x" step="1">
            </div>
          </div>
          <div>
            <label>Y</label>
            <div>
              <input type="number" v-model="element.y" step="1">
            </div>
          </div>
          <div>
            <label>Z</label>
            <div>
              <input type="number" v-model="element.z" step="1">
            </div>
          </div>
          <div>
            <label>Width</label>
            <div>
              <input type="number" v-model="element.width" step="1">
            </div>
          </div>
          <div>
            <label>Height</label>
            <div>
              <input type="number" v-model="element.height" step="1">
            </div>
          </div>
          <div>
            <label>Layer</label>
            <div>
              <input type="number" v-model="element.layer" step="1">
            </div>
          </div>
        </div>
        <div>
          <label>Element is</label>
          <select v-model="element.stateHandling">
            <option value="show for any">Shown for any of these states</option>
            <option value="show for all">Shown for all of these states</option>
            <option value="hide for any">Hidden for any of these states</option>
            <option value="hide for all">Hidden for all of these states</option>
          </select>
          <div style="display:flex;flex-wrap:wrap">
            <span v-for="(checked, state) in getModifiableStates(element.states)" :key="state">
              <input type="checkbox" v-model="element.states[state]"> <label class="inline-label">{{ state }}</label>
            </span>
            <span v-for="(checked, state) in getAudioStates(element.states)" :key="state">
              <input type="checkbox" v-model="element.states[state]"> <label class="inline-label">"{{ getAudioStateAsset(state).name }}" playing</label>
            </span>
          </div>
        </div>
        <label>Click Action</label>
        <select class="action-select" v-for="index in Object.keys(element.action)" :key="index" v-model="element.action[index]" @update:modelValue="clearActions(element)">
          <option value="" selected>(none)</option>
          <template v-if="element.action.length <= 1">
            <option :value="'goto:prev'">Go to Previous Layer</option>
            <option :value="'goto:next'">Go to Next Layer</option>
            <option v-for="(population, layerKey) in getLayerPopulation(true)" :key="layerKey" :value="'goto:' + layerKey">Go to Layer {{ layerKey }}<template v-if="getLayerName(layerKey)"> [{{ getLayerName(layerKey) }}]</template><template v-if="!population"> (empty)</template></option>
          </template>
          <option value="state:,0,1">Clear All States</option>
          <option v-for="(checked, state) in layerSettings.states" :key="state" :value="'state:' + [state, 1].join(',')">Set '{{ state }}' State</option>
          <option v-for="(checked, state) in layerSettings.states" :key="state" :value="'state:' + [state, 'toggle'].join(',')">Toggle '{{ state }}' State</option>
          <option v-for="(checked, state) in layerSettings.states" :key="state" :value="'state:' + [state, 0].join(',')">Clear '{{ state }}' State</option>
          <option v-for="media_id in audioMediaIds" :key="media_id" :value="'audio:' + media_id">Toggle Audio: {{ s.assets[media_id] ? s.assets[media_id].name : 'No Title' }}</option>
          <template v-if="element.action.length <= 1">
            <option v-for="(name, action) in getLinkActions()" :key="action" :value="action">Open: {{ name }}</option>
            <option value="url">Custom URL</option>
          </template>
        </select>
        <select class="action-select" v-if="elementCanHaveMultipleActions(element)" @input="e => addAction(e.target.value, element, e)">
          <option value="" selected>(none)</option>
          <template v-if="element.action.length <= 0">
            <option :value="'goto:prev'">Go to Previous Layer</option>
            <option :value="'goto:next'">Go to Next Layer</option>
            <option v-for="(population, layerKey) in getLayerPopulation(true)" :key="layerKey" :value="'goto:' + layerKey">Go to Layer {{ layerKey }}<template v-if="getLayerName(layerKey)"> [{{ getLayerName(layerKey) }}]</template><template v-if="!population"> (empty)</template></option>
          </template>
          <option value="state:,0,1">Clear All States</option>
          <option v-for="(checked, state) in getModifiableStates(layerSettings.states)" :key="state" :value="'state:' + [state, 1].join(',')">Set '{{ state }}' State</option>
          <option v-for="(checked, state) in getModifiableStates(layerSettings.states)" :key="state" :value="'state:' + [state, 'toggle'].join(',')">Toggle '{{ state }}' State</option>
          <option v-for="(checked, state) in getModifiableStates(layerSettings.states)" :key="state" :value="'state:' + [state, 0].join(',')">Clear '{{ state }}' State</option>
          <option v-for="media_id in audioMediaIds" :key="media_id" :value="'audio:' + media_id">Toggle Audio: {{ s.assets[media_id] ? s.assets[media_id].name : 'No Title' }}</option>
          <template v-if="element.action.length <= 0">
            <option v-for="(name, action) in getLinkActions()" :key="action" :value="action">Open: {{ name }}</option>
            <option value="url">Custom URL</option>
          </template>
        </select>
        <div v-if="element.action[0] === 'url'">
          <label>Custom URL</label>
          <div>
            <input type="text" v-model="element.url" style="width:100%">
          </div>
        </div>
      </PropertyPage>
    </div>
    <div v-if="editMode" class="non-work-area bottom">
      <PropertyPage v-for="(element, index) in s.elements" :key="index + '-' + element.id" :visible="element && currentElement && d.propertyPageVisible && element.id === currentElement.id" :showTitle="false" :titleExtra="element.id" @close="closeProperties">
        <template v-if="element.type === 'text'">
          <div class="bottom-textarea">
            <label>Text</label>
            <textarea rows="12" cols="50" v-model="element.value" />
          </div>
        </template>
        <template v-if="element.type === 'button'">
          <label>Button Text</label>
          <input type="text" v-model="element.value" />
        </template>
        <template v-if="element.type === 'image' || element.type === 'video'">
          <div :class="{
            'asset-sort': true,
            'reverse': d.assetSortReverse,
          }" role="button" tabindex="0" @click="d.assetSortReverse = !d.assetSortReverse" :title="d.assetSortReverse ? 'Newest First - Click to Reverse' : 'Oldest First - Click to Reverse'" :aria-label="d.assetSortReverse ? 'Newest First. Click to Reverse.' : 'Oldest First. Click to Reverse.'"></div>
        </template>
        <template v-if="element.type === 'image'">
          <ImageSelect v-model="element.value" :data="s.assets" :assetSortReverse="d.assetSortReverse" />
        </template>
        <template v-if="element.type === 'video'">
          <VideoSelect v-model="element.value" :data="s.assets" :assetSortReverse="d.assetSortReverse" />
        </template>
      </PropertyPage>
    </div>
  </div>
</template>

<style>
  .exhibit-frame-app {
    font-size: 16px;
    user-select: none;
    position: relative;
  }

  /* non-scoped styles */
  .exhibit-frame-app * {
    margin: 0;
    padding: 0;
    line-height: 1.15;
    outline: none;
  }

  .exhibit-frame-app .interact-element {
    outline: none;
  }

  .exhibit-frame-app .interact-element:not(.replace):focus {
    filter: url(#outline);
  }

  .exhibit-frame-app .interact-element.replace {
    filter: url(#noRender);
  }

  .exhibit-frame-app .interact-element.replace:focus {
    filter: url(#outlineOnly);
  }

  .exhibit-frame-app input, .exhibit-frame-app textarea {
    border: none;
    outline: none;
  }

  .exhibit-frame-app .menu-divider {
    margin: 0.25em 0.25em;
    border-top: 1px solid rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 1.0);
  }

  .exhibit-frame-app .no-value {
    color: #757575 !important;
    fill: #757575 !important;
  }

  .exhibit-frame-app .no-interact {
    pointer-events: none !important;
  }

  .exhibit-frame-app .inline-label {
    display: inline;
  }

  .exhibit-frame-app label,
  .exhibit-frame-app .label {
    display: inline;
    vertical-align: middle;
    margin: 0.25em 0.5em 0.25em 0;
  }

  .exhibit-frame-app input,
  .exhibit-frame-app select {
    display: inline-block;
    vertical-align: middle;
    margin: 0.25em 0;
  }

  .exhibit-frame-app input:not([type="color"]),
  .exhibit-frame-app select,
  .exhibit-frame-app textarea {
    border: 1px solid #000;
    border-radius: 2px;
    padding: 0.25em;
  }

  .exhibit-frame-app textarea {
    width: 100%;
  }

  .exhibit-frame-app input[type="color"] {
    border: none;
    background: none;
  }

  .exhibit-frame-app .fs-smallest {
    font-size: 11px;
  }

  .exhibit-frame-app .fs-small {
    font-size: 14px;
  }

  .exhibit-frame-app .fs-bigger {
    font-size: 20px;
  }

  .exhibit-frame-app .fs-large {
    font-size: 24px;
  }

  .exhibit-frame-app .fs-larger {
    font-size: 36px;
  }

  .exhibit-frame-app .fs-largest {
    font-size: 56px;
  }

  .exhibit-frame-app .fs-title {
    font-size: 67px;
  }

  .exhibit-frame-app .fw-bold {
    font-weight: 700;
  }


  .exhibit-frame-app .ta-center {
    text-align: center;
  }

  .exhibit-frame-app .ta-right {
    text-align: right;
  }

  .exhibit-frame-app .ta-justify {
    text-align: justify;
  }
</style>

<style scoped>
  .exhibit-frame-app.edit-mode .work-area {
    outline: 1px dashed #757575;
  }

  .work-area {
    position: relative;
    overflow: visible;
    z-index: 1;
  }

  .non-work-area {
    position: relative;
    font-size: 12px;
    line-height: 1;
    background-color: #e0e0e0;
  }

  .non-work-area.top {
    padding: .5em 1em;
  }

  .non-work-area.right {
    position: absolute;
    left: 100%;
    top: 0;
    height: 100%;
  }

  .non-work-area.left {
    position: absolute;
    right: 100%;
    top: 0;
    height: 100%;
  }

  .non-work-area.bottom {
    height: 130px;
    display: flex;
    flex-direction: column;
  }

  .non-work-area.bottom > * {
    flex: 1 1 auto;
  }

  .exhibit-frame-svg {
    position: relative;
    width: 100%;
    pointer-events: all;
    z-index: 0;
  }

  .exhibit-frame-overlay {
    position: absolute;
    z-index: 500;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    overflow: visible;
    white-space: nowrap;
  }

  .edit-stats {
    position: absolute;
    top: 0.125em;
    color: #757575;
    text-shadow: 1px 1px #fff, -1px -1px #fff, -1px 1px #fff, 1px -1px #fff;
  }

  .edit-stats.right {
    right: 0.25em;
  }

  .edit-stats.left {
    left: 0.25em;
  }

  .stats-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: baseline;
  }

  .stats-row * {
    flex: 0 0 auto;
  }

  .input-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: baseline;
    margin: 0 -0.25em;
  }

  .input-row > * {
    flex: 0 0 auto;
    margin: 0.25em;
  }

  .property-table {
    display: table;
  }

  .property-table > * {
    display: table-row;
  }

  .property-table > * > * {
    display: table-cell;
    text-align: right;
  }

  .property-table > * > *:first-child {
    text-align: left;
    padding-right: 0.5em;
  }

  .property-table > * > * > input,
  .property-table > * > * > select {
    display: block;
    width: 100%;
  }

  .action-select {
    display: block;
  }

  .bottom-textarea {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .bottom-textarea > label {
    flex: 0 0 auto;
  }

  .bottom-textarea > textarea {
    flex: 1 1 1px;
  }

  .svg-text {
    white-space: pre-wrap;
    user-select: auto;
    cursor: default;
  }

  .svg-video {
    user-select: none;
    cursor: default;
    text-align: center;
  }

  audio {
    display: none;
  }

  .volume-control {
    margin: 0.25em 0.5em;
    white-space: nowrap;
  }

  .splash-input {
    margin: 0.25em 0.5em;
    white-space: nowrap;
  }

  .splash-input label {
    display: inline-block;
    min-width: 135px;
  }

  @keyframes splash {
    0% {
      opacity: 1;
    }

    50% {
      opacity: 1;
    }

    90% {
      opacity: 0;
    }

    100% {
      opacity: 0;
    }
  }

  @keyframes splash-title {
    0% {
      opacity: 0;
    }

    50% {
      opacity: 1;
    }

    100% {
      opacity: 1;
    }
  }

  .splash-screen {
    display: block;
    width: 100%;
    height: 100%;
    animation: splash 4s;
    animation-fill-mode: backwards;
    animation-iteration-count: 1;
    opacity: 0;
    background-color: #000;
  }

  .splash-screen .title-wrapper {
    position: relative;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }

  .splash-screen .title {
    animation: splash-title 4s;
    animation-iteration-count: 1;
    animation-fill-mode: backwards;
    color: #fff;
    white-space: pre;
  }

  .splash-screen .subtitle {
    animation: splash-title 4s;
    animation-iteration-count: 1;
    animation-fill-mode: backwards;
    color: #fff;
    white-space: pre;
  }

  .drag-handle {
    fill: #fff;
    stroke: #000;
    stroke-width: 2px;
    cursor: move;
  }

  .drag-handle.ns {
    cursor: ns-resize;
  }

  .drag-handle.ew {
    cursor: ew-resize;
  }

  .drag-handle.nwse {
    cursor: nwse-resize;
  }

  .drag-handle.nesw {
    cursor: nesw-resize;
  }

  .asset-sort {
    position: absolute;
    left: 2px;
    top: 2px;
    border-top: 0 solid #000;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 0px solid #000;
    cursor: pointer;

    border-top-width: 0;
    border-bottom-width: 12px;
  }

  .asset-sort.reverse {
    border-top-width: 12px;
    border-bottom-width: 0;
  }
</style>
